import { useState } from 'react';

/**
 * A remote image that degrades instead of showing the browser's broken-image glyph.
 *
 * `src` comes back `undefined` when there is no URL (`null` and `''` included) or once that URL
 * failed to load, so the caller renders its own
 * placeholder in the same branch it already uses for "no photo". The failure belongs to the
 * URL, not to the component: a list that reuses the same slot for another item, or a refreshed
 * snapshot with a new URL, gets its own attempt without an effect.
 *
 *     const photo = useImageFallback(trip.photoUrl);
 *     return photo.src ? <img src={photo.src} onError={photo.onError} alt="" /> : <Placeholder />;
 */
export function useImageFallback(src: string | null | undefined): {
  readonly src: string | undefined;
  readonly onError: () => void;
} {
  const [brokenSrc, setBrokenSrc] = useState<string>();
  const usable = src && src !== brokenSrc ? src : undefined;
  return {
    src: usable,
    onError: () => {
      if (usable) setBrokenSrc(usable);
    },
  };
}
